import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useLegalSettings } from '../hooks/useLegalSettings';

const LAST_UPDATED = 'December 2025';

export default function PrivacyPage() {
  const { business_name, abn, contact_email } = useLegalSettings();
  return (
    <div className="max-w-3xl mx-auto fade-in" data-testid="privacy-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text">Privacy Policy</h1>
          <p className="text-text-muted text-sm">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8 space-y-6 text-text leading-relaxed">
        <section>
          <p>
            This Privacy Policy explains how <strong>{business_name}</strong> (ABN <strong>{abn}</strong>) ("we", "us", "our") collects, uses, stores and discloses your personal information when you use the Digital Safety Course and our website. We are an Australian sole trader and we comply with the <strong>Australian Privacy Principles (APPs)</strong> set out in the <em>Privacy Act 1988 (Cth)</em>.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">1. What personal information we collect</h2>
          <p>We only collect what we need to run the Course:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li><strong>Account details</strong> — your first name, email address, and password (stored as a one-way hash, never in plain text)</li>
            <li><strong>Course progress</strong> — which modules you've completed, your quiz scores, checklist ticks, and the notes you type into the course</li>
            <li><strong>Certificate information</strong> — the full name you confirm for your certificate when you finish the Course</li>
            <li><strong>Payment information</strong> — your purchase is processed by Stripe. We receive confirmation that you paid and the Stripe payment ID; we do <strong>not</strong> see or store your card details</li>
            <li><strong>Basic technical data</strong> — standard server logs (IP address, browser, timestamps) used for security and troubleshooting</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">2. Why we collect it</h2>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>to create and secure your account</li>
            <li>to save your progress so you can learn on any device and pick up where you left off</li>
            <li>to confirm your purchase and unlock the Course</li>
            <li>to issue your completion certificate</li>
            <li>to send transactional emails (confirm your email, reset your password, receipt)</li>
            <li>to protect the service against fraud, abuse or security threats</li>
          </ul>
          <p className="mt-2">We do not sell your personal information. We do not use it for advertising profiling.</p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">3. Who we share information with</h2>
          <p>We only share your information with the service providers we need to run the Course:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li><strong>Stripe</strong> — to take your payment securely. Stripe handles card data under its own privacy terms.</li>
            <li><strong>Our email provider (Resend)</strong> — to send you transactional emails (verification, password reset, receipts).</li>
            <li><strong>Our hosting provider</strong> — to run the website and database infrastructure.</li>
          </ul>
          <p className="mt-2">
            Some of these providers are based overseas, which means your information may be stored or processed outside Australia. We only use providers who offer protections consistent with the Australian Privacy Principles.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">4. How we protect your information</h2>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>passwords are stored as bcrypt hashes (not reversible)</li>
            <li>all traffic to the website is encrypted with HTTPS</li>
            <li>admin access is protected with two-factor authentication</li>
            <li>only the people who need it have access to the account database</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">5. How long we keep it</h2>
          <p>
            We keep your account and progress for as long as you have an active account. If you ask us to delete your account, we remove your personal information within 30 days, except where we are required to keep certain records (for example, tax or payment records required by Australian law).
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">6. Your rights</h2>
          <p>Under the Australian Privacy Principles you can:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>ask what personal information we hold about you</li>
            <li>ask us to correct information that is wrong or out of date</li>
            <li>ask us to delete your account and personal information</li>
            <li>complain if you believe we've mishandled your information</li>
          </ul>
          <p className="mt-2">
            Email us at <a href={`mailto:${contact_email}`} className="text-primary font-semibold hover:underline">{contact_email}</a> and we'll respond within 30 days. If you're not satisfied with our response, you can contact the Office of the Australian Information Commissioner (OAIC) at <a className="text-primary font-semibold hover:underline" href="https://www.oaic.gov.au" target="_blank" rel="noreferrer">oaic.gov.au</a>.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">7. Cookies and local storage</h2>
          <p>
            We use browser storage to remember whether you're signed in and to save progress for visitors who haven't created an account yet. We do not use third-party advertising cookies.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">8. Children</h2>
          <p>
            The Course is designed for adults. We do not knowingly collect information from children under 15 without a parent or guardian present. If you believe a child has created an account without consent, contact us and we will remove it.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">9. Changes to this Policy</h2>
          <p>
            We may update this Policy from time to time. The "last updated" date at the top of this page shows when we last changed it. For material changes we'll email account holders.
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-xl mb-2">10. Contact</h2>
          <p>
            Questions, requests or complaints about privacy? Contact us at <a href={`mailto:${contact_email}`} className="text-primary font-semibold hover:underline">{contact_email}</a>.
          </p>
          <p className="mt-3 text-sm text-text-muted">
            See also our <Link to="/terms" className="text-primary font-semibold hover:underline">Terms of Service</Link> and <Link to="/refund" className="text-primary font-semibold hover:underline">Refund Policy</Link>.
          </p>
        </section>
      </div>
    </div>
  );
}
